import { loadScript } from 'dashboard/helper/DOMHelpers';

export const EMBEDDED_SIGNUP_FINISH_EVENTS = [
  'FINISH',
  'FINISH_ONLY_WABA',
  'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
  'FINISH_OBO_MIGRATION',
  'FINISH_GRANT_ONLY_API_ACCESS',
];

export const isEmbeddedSignupFinishEvent = eventType => {
  return EMBEDDED_SIGNUP_FINISH_EVENTS.includes(eventType);
};

export const extractEmbeddedSignupError = data => {
  if (!data || typeof data !== 'object') return null;

  if (data.event === 'CANCEL' && data.data?.error_message) {
    return {
      message: data.data.error_message,
      code: data.data.error_code,
      sessionId: data.data.session_id,
      timestamp: data.data.timestamp,
    };
  }

  if (data.event === 'error') {
    return {
      message: data.error_message || data.data?.error_message,
      code: data.error_code || data.data?.error_code,
      sessionId: data.session_id || data.data?.session_id,
      timestamp: data.timestamp || data.data?.timestamp,
    };
  }

  return null;
};

export const loadFacebookSdk = async () => {
  return loadScript('https://connect.facebook.net/pt_BR/sdk.js', {
    async: true,
    defer: true,
    crossOrigin: 'anonymous',
  });
};

export const initializeFacebook = (appId, apiVersion) => {
  const version = apiVersion || 'v25.0';
  return new Promise(resolve => {
    const init = () => {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version,
      });
      resolve();
    };

    if (window.FB) {
      init();
    } else {
      window.fbAsyncInit = init;
    }
  });
};

// Only waba_id is guaranteed: Meta's coexistence FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING
// event documents data: { waba_id } alone — no business_id or phone_number_id.
export const isValidBusinessData = businessData => {
  return Boolean(businessData && businessData.waba_id);
};

const COEXISTENCE_FINISH_EVENT = 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';

const FINISH_EVENTS = ['FINISH', COEXISTENCE_FINISH_EVENT];

// Terminal events that end the flow without a Cloud API phone number we can build an
// inbox from. Embedded Signup v4 can emit any of them depending on the products
// enabled on the configuration; without an explicit branch the popup simply closes
// and the caller waits on a promise that never settles.
const UNSUPPORTED_FINISH_EVENTS = [
  'FINISH_ONLY_WABA',
  'FINISH_OBO_MIGRATION',
  'FINISH_GRANT_ONLY_API_ACCESS',
];

export const SIGNUP_RESULT = Object.freeze({
  FINISH: 'finish',
  UNSUPPORTED: 'unsupported',
  CANCEL: 'cancel',
  ERROR: 'error',
  IGNORE: 'ignore',
});

// Maps a WA_EMBEDDED_SIGNUP payload onto the outcomes callers act on. v4 spells the
// explicit failure event `ERROR` where v3 used `error`, and also reports user-facing
// failures as a CANCEL carrying an error_message — a bare CANCEL is a deliberate
// dismissal, so the two have to be told apart rather than both read as "cancelled".
export const classifySignupEvent = data => {
  const event = data?.event;
  if (typeof event !== 'string') return { type: SIGNUP_RESULT.IGNORE };

  const errorMessage = data?.error_message;

  if (FINISH_EVENTS.includes(event)) {
    return {
      type: SIGNUP_RESULT.FINISH,
      isCoexistence: event === COEXISTENCE_FINISH_EVENT,
    };
  }

  if (UNSUPPORTED_FINISH_EVENTS.includes(event)) {
    return { type: SIGNUP_RESULT.UNSUPPORTED };
  }

  if (event.toUpperCase() === 'ERROR') {
    return { type: SIGNUP_RESULT.ERROR, errorMessage };
  }

  if (event === 'CANCEL') {
    return errorMessage
      ? { type: SIGNUP_RESULT.ERROR, errorMessage }
      : { type: SIGNUP_RESULT.CANCEL };
  }

  return { type: SIGNUP_RESULT.IGNORE };
};

export const createMessageHandler = onEmbeddedSignupData => {
  return event => {
    let hostname;
    try {
      hostname = new URL(event.origin).hostname;
    } catch {
      return;
    }
    if (hostname !== 'facebook.com' && !hostname.endsWith('.facebook.com'))
      return;

    try {
      let data;
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else if (typeof event.data === 'object' && event.data !== null) {
        data = event.data;
      } else {
        return;
      }

      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        onEmbeddedSignupData(data);
      }
    } catch {
      // Ignore non-JSON or irrelevant messages
    }
  };
};

const EMBEDDED_SIGNUP_FEATURE_TYPE = 'whatsapp_business_app_onboarding';
const EMBEDDED_SIGNUP_SESSION_INFO_VERSION = '3';

export const initWhatsAppEmbeddedSignup = (configId, options = {}) => {
  const { isCoexistenceEnabled = false, oauthRedirectUri = '' } = options;

  const extras = {
    setup: {},
  };

  if (isCoexistenceEnabled) {
    extras.featureType = EMBEDDED_SIGNUP_FEATURE_TYPE;
    extras.sessionInfoVersion = EMBEDDED_SIGNUP_SESSION_INFO_VERSION;
  }

  return new Promise((resolve, reject) => {
    const loginOptions = {
      config_id: configId,
      response_type: 'code',
      override_default_response_type: true,
      extras,
    };

    if (oauthRedirectUri) {
      loginOptions.redirect_uri = oauthRedirectUri;
    }

    window.FB.login(response => {
      if (response.authResponse && response.authResponse.code) {
        resolve(response.authResponse.code);
      } else if (response.error) {
        reject(new Error(response.error));
      } else {
        reject(new Error('Login cancelled'));
      }
    }, loginOptions);
  });
};

export const setupFacebookSdk = async (appId, apiVersion) => {
  const version = apiVersion || 'v25.0';
  await loadFacebookSdk();
  await initializeFacebook(appId, version);
};
